import {Injectable, Injector} from '@angular/core';
import {catchError, Observable, of, tap} from 'rxjs';
import {BaseService} from './base.service';
import {FeedbackPage, FeedbackResponse} from '@twpub/core/models';
import {SharedStateService} from './shared-state.service';
import {BrowserStorageUtils, STORAGE_KEYS} from '@twpub/shared/utils/browser-storage.utils';

@Injectable({
  providedIn: 'root'
})
export class FeedbackService extends BaseService {
  constructor(injector: Injector, private sharedState: SharedStateService) {
    super(injector);
  }

  /**
   * Gets feedback page configuration from server.
   * Caches in localStorage as fallback for offline scenarios.
   */
  getFeedbackConfig(): Observable<FeedbackPage | null> {
    return this.http.get<FeedbackPage>(this.url('/feedback/config')).pipe(
      tap(config => {
        if (config) {
          BrowserStorageUtils.setLocalStorage(STORAGE_KEYS.FEEDBACK_CONFIG_KEY, JSON.stringify(config));
        }
      }),
      catchError(err => {
        this.logger.warn('Failed to fetch feedback config, trying cache:', err);
        return of(this.getCachedFeedbackConfig());
      })
    );
  }

  /**
   * Returns cached feedback configuration from localStorage.
   */
  getCachedFeedbackConfig(): FeedbackPage | null {
    const cached = BrowserStorageUtils.getLocalStorage(STORAGE_KEYS.FEEDBACK_CONFIG_KEY);
    if (cached) {
      try {
        return JSON.parse(cached) as FeedbackPage;
      } catch (e) {
        this.logger.error('Failed to parse cached feedback config:', e);
      }
    }
    return null;
  }

  /**
   * Submits feedback to the server.
   *
   * @param name sender name
   * @param email sender email
   * @param subject feedback subject
   * @param message feedback message
   * @param attachment optional file attachment
   * @param dictionaryId optional dictionary ID (defaults to current dictionary from shared state)
   */
  submitFeedback(
    name: string,
    email: string,
    subject: string,
    message: string,
    attachment?: File,
    dictionaryId?: number
  ): Observable<FeedbackResponse> {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    formData.append('subject', subject);
    formData.append('message', message);

    // Use provided dictionaryId or get from shared state
    const dictId = dictionaryId ?? this.getCurrentDictionaryId();
    if (dictId) {
      formData.append('dictionaryId', dictId.toString());
    }

    if (attachment) {
      formData.append('attachment', attachment, attachment.name);
    }

    return this.http.post<FeedbackResponse>(this.url('/feedback'), formData);
  }

  /**
   * Gets current dictionary ID from shared state.
   */
  private getCurrentDictionaryId(): number | undefined {
    const dictIds = this.sharedState.sessionObj?.dictIds;
    return dictIds && dictIds.length > 0 ? dictIds[0] : undefined;
  }

  /**
   * Formats file size in human-readable format.
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
